import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import StatsIcon from 'components/utils/iconComponents/StatsIcon';
import LoadingIconComponent from 'components/Orders/iconComponent/LoadingIconComponent';
import { REPORT_SALES_QUERY_KEY, useReportSales } from 'hooks/useReportSales';
import {
  REPORT_SALES_TOTALS_QUERY_KEY,
  useReportSalesTotals,
} from 'hooks/useReportSalesTotals';
import styles from './StatisticsPage.module.css';
import SalesReportTable from './SalesReportTable';
import { downloadReportSales } from './downloadReportSales';
import { REPORT_SALES_PAGE_SIZE } from 'utils/reportSalesUtils';

const StatisticsPage = () => {
    const queryClient = useQueryClient();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState(null);

    const {
        data: salesData,
        isLoading: isSalesLoading,
        isFetching: isSalesFetching,
        error: salesError,
    } = useReportSales(
        appliedFilters
            ? { ...appliedFilters, page: currentPage }
            : null
    );

    const {
        data: totalsData,
        isLoading: isTotalsLoading,
        isFetching: isTotalsFetching,
        error: totalsError,
    } = useReportSalesTotals(appliedFilters);

    useEffect(() => {
        if (!isGenerating) return;

        const queriesSettled =
            !isSalesFetching &&
            !isSalesLoading &&
            !isTotalsFetching &&
            !isTotalsLoading;

        if (queriesSettled) {
            setIsGenerating(false);
        }
    }, [
        isGenerating,
        isSalesFetching,
        isSalesLoading,
        isTotalsFetching,
        isTotalsLoading,
    ]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!startDate || !endDate) return;

        const isSameRange =
            appliedFilters?.startDate === startDate &&
            appliedFilters?.endDate === endDate;

        setIsGenerating(true);
        setHasSubmitted(true);
        setCurrentPage(1);
        setExportError(null);
        setAppliedFilters({ startDate, endDate });

        if (isSameRange) {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: [REPORT_SALES_QUERY_KEY, startDate, endDate],
                }),
                queryClient.invalidateQueries({
                    queryKey: [REPORT_SALES_TOTALS_QUERY_KEY, startDate, endDate],
                }),
            ]);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleExport = async () => {
        if (!appliedFilters) return;

        setIsExporting(true);
        setExportError(null);

        try {
            await downloadReportSales(appliedFilters.startDate, appliedFilters.endDate);
        } catch (error) {
            setExportError(
                error?.response?.status === 404
                    ? 'No hay registros para exportar en el período seleccionado.'
                    : 'No se pudo exportar el reporte. Intentá nuevamente.'
            );
        } finally {
            setIsExporting(false);
        }
    };

    const sales = isGenerating ? [] : (salesData?.sales ?? []);
    const total = isGenerating ? 0 : (salesData?.total ?? totalsData?.total ?? 0);
    const totals = isGenerating ? null : (totalsData?.totals ?? null);
    const error = salesError || totalsError;
    const isReportLoading = isGenerating;

    const canExport =
        hasSubmitted &&
        appliedFilters &&
        !isReportLoading &&
        total > 0 &&
        !isExporting;

    return (
        <div className='font-sans h-full flex flex-col min-h-0 overflow-hidden pt-4'>
            <div className='flex-none flex font-semibold text-sm text-gray-500 items-center h-[60px] w-full gap-4 shrink-0'>
                <div className='size-14 flex-none'><StatsIcon /></div>
                <div className='size-14 grow'>Reporte de ventas</div>
                <div className='grow flex justify-end'>
                    <form className='flex items-end gap-3' onSubmit={handleSubmit}>
                        <div className='flex flex-col gap-1'>
                            <label htmlFor='stats-date-from' className='text-xs font-medium text-gray-500'>
                                Desde
                            </label>
                            <input
                                id='stats-date-from'
                                type='date'
                                value={startDate}
                                onChange={(event) => setStartDate(event.target.value)}
                                className={styles.dateInput}
                                required
                            />
                        </div>
                        <div className='flex flex-col gap-1'>
                            <label htmlFor='stats-date-to' className='text-xs font-medium text-gray-500'>
                                Hasta
                            </label>
                            <input
                                id='stats-date-to'
                                type='date'
                                value={endDate}
                                min={startDate || undefined}
                                onChange={(event) => setEndDate(event.target.value)}
                                className={styles.dateInput}
                                required
                            />
                        </div>
                        <button
                            type='submit'
                            disabled={isReportLoading || !startDate || !endDate}
                            className='h-10 bg-primary-700 text-white px-4 rounded-md text-sm font-semibold hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-700 focus:ring-offset-1 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center'
                        >
                            {isReportLoading ? (
                                <>
                                    <LoadingIconComponent className='text-white' />
                                    Generando...
                                </>
                            ) : (
                                'Generar'
                            )}
                        </button>
                        <button
                            type='button'
                            onClick={handleExport}
                            disabled={!canExport}
                            className='h-10 border border-gray-300 bg-white text-gray-700 px-4 rounded-md text-sm font-semibold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-700 focus:ring-offset-1 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center'
                        >
                            {isExporting ? (
                                <>
                                    <LoadingIconComponent className='text-gray-500' />
                                    Exportando...
                                </>
                            ) : (
                                'Exportar Excel'
                            )}
                        </button>
                    </form>
                </div>
            </div>

            {exportError && (
                <div className='flex-none mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
                    {exportError}
                </div>
            )}

            {error && (
                <div className='flex-none mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
                    {error.message || 'No se pudo cargar el reporte de ventas. Intentá nuevamente.'}
                </div>
            )}

            <div className='flex-1 min-h-0 mt-4 flex flex-col'>
                <SalesReportTable
                rows={sales}
                totals={totals}
                totalsLoading={!isGenerating && isTotalsLoading}
                loading={isReportLoading}
                hasSubmitted={hasSubmitted}
                pagination={{
                    currentPage,
                    pageSize: REPORT_SALES_PAGE_SIZE,
                    totalItems: total,
                    onPageChange: handlePageChange,
                    loading: !isGenerating && isSalesFetching,
                }}
                />
            </div>
        </div>
    );
};

export default StatisticsPage;
